import { useQuery, useMutation } from '@apollo/client';
import { GET_USER_BY_ID, TOGGLE_FOLLOW, UPDATE_PROFILE } from '../graphql/queries';
import { UserProfile, GetUserByIdVariables, ToggleFollowResponse, ToggleFollowVariables, UpdateProfileResponse, UpdateProfileVariables, UploadProfilePhotoResponse } from '../types';
import { toast } from 'react-toastify';
import { useTranslation } from '@/hooks/useTranslation';
import { NAMESPACES } from '@/i18n/namespaces';
import { useState, useCallback } from 'react';
import { APP_BACKEND_URL } from '@/utils/config';

export const useGetUserById = (userId: string) => {
  const { t: tErrors } = useTranslation(NAMESPACES.ERRORS);
  
  return useQuery<{ userById: UserProfile }, GetUserByIdVariables>(GET_USER_BY_ID, {
    variables: { userId },
    fetchPolicy: 'cache-and-network',
    skip: !userId, // Skip query if userId is not provided
    onError: (error) => {
      console.error('Error fetching user:', error);
      
      // Check for specific GraphQL errors
      const graphQLErrors = error.graphQLErrors || [];
      
      for (const err of graphQLErrors) {
        const extensions = err.extensions || {};
        const className = extensions.class_name as string;
        
        // Handle specific error types
        switch (className) {
          case 'App\\Exceptions\\NotFoundExceptionException':
            toast.error('User not found');
            return;
          default:
            toast.error(err.message || tErrors('graphql.default'));
            return;
        }
      }
      
      // For network errors or other issues
      toast.error(tErrors('graphql.default'));
    },
  });
};

export const useToggleFollow = () => {
  const { t: tErrors } = useTranslation(NAMESPACES.ERRORS);
  
  return useMutation<{ toggleFollow: ToggleFollowResponse }, ToggleFollowVariables>(TOGGLE_FOLLOW, {
    onError: (error) => {
      console.error('Error toggling follow:', error);
      
      // Check for specific GraphQL errors
      const graphQLErrors = error.graphQLErrors || [];
      
      for (const err of graphQLErrors) {
        const extensions = err.extensions || {};
        const className = extensions.class_name as string;
        
        // Handle specific error types
        switch (className) {
          case 'App\\Exceptions\\NotFoundExceptionException':
            toast.error('User not found');
            return;
          case 'App\\Exceptions\\UnauthorizedException':
            toast.error('You must be logged in to follow users');
            return;
          default:
            toast.error(err.message || tErrors('graphql.default'));
            return;
        }
      }
      
      // For network errors or other issues
      toast.error(tErrors('graphql.default'));
    },
  });
};

export const useUpdateProfile = () => {
  const { t: tErrors } = useTranslation(NAMESPACES.ERRORS);
  
  return useMutation<{ updateProfile: UpdateProfileResponse }, UpdateProfileVariables>(UPDATE_PROFILE, {
    onError: (error) => {
      console.error('Error updating profile:', error);
      
      // Check for specific GraphQL errors
      const graphQLErrors = error.graphQLErrors || [];
      
      for (const err of graphQLErrors) {
        const extensions = err.extensions || {};
        const className = extensions.class_name as string;
        
        // Handle specific error types
        switch (className) {
          case 'App\\Exceptions\\UnauthorizedException':
            toast.error('You must be logged in to update your profile');
            return;
          case 'App\\Exceptions\\ValidationException':
            toast.error(err.message || 'Please check your input and try again');
            return;
          default:
            toast.error(err.message || tErrors('graphql.default'));
            return;
        }
      }
      
      // For network errors or other issues
      toast.error(tErrors('graphql.default'));
    },
  });
};


export const useUploadProfilePhoto = () => {
  const { t: tErrors } = useTranslation(NAMESPACES.ERRORS);
  const [loading, setLoading] = useState(false);
  
  const uploadProfilePhoto = useCallback(async (file: File): Promise<{ data?: { uploadProfilePhoto: UploadProfilePhotoResponse } }> => {
    setLoading(true);
    
    const graphqlUrl = `${APP_BACKEND_URL}/graphql`;
    
    try {
      console.log('🚀 Starting photo upload...', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });

      // Create FormData for multipart upload per GraphQL multipart request spec
      const formData = new FormData();
      
      // GraphQL operation
      const operations = JSON.stringify({
        query: `
          mutation UploadProfilePhoto($photo: Upload!) {
            uploadProfilePhoto(photo: $photo) {
              success
              message
              user {
                id
                name
                nickname
                bio
                profile_photo_urls {
                  original
                  thumbnail
                  small
                  medium
                  large
                }
                followers_count
                following_count
                applauses_count
                is_me
              }
            }
          }
        `,
        variables: { photo: null }
      });
      
      // Map which variables correspond to which files
      const map = JSON.stringify({
        "0": ["variables.photo"]
      });
      
      // Add to FormData according to GraphQL multipart request specification
      formData.append("operations", operations);
      formData.append("map", map);
      formData.append("0", file);

      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 30000); // 30 second timeout
      
      try {
        // Send request with proper headers for multipart upload
        const response = await fetch(graphqlUrl, {
          method: 'POST',
          credentials: 'include', // Include cookies for authentication
          headers: {
            'X-Requested-With': 'XMLHttpRequest', // Critical header for AJAX requests
          },
          body: formData,
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('❌ HTTP Error:', {
            status: response.status,
            statusText: response.statusText,
            body: errorText
          });
          throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
        }
        
        const result = await response.json();
        
        // Check for GraphQL errors
        if (result.errors && result.errors.length > 0) {
          const error = result.errors[0];
          console.error('❌ GraphQL Error:', error);
          const extensions = error.extensions || {};
          const className = extensions.class_name as string;
          
          // Handle specific error types
          switch (className) {
            case 'App\\Exceptions\\UnauthorizedException':
              toast.error('You must be logged in to upload a photo');
              throw new Error('Unauthorized');
            case 'App\\Exceptions\\ValidationException':
              toast.error(error.message || 'Please select a valid image file');
              throw new Error('Validation failed');
            case 'App\\Exceptions\\FileTooLargeException':
              toast.error('File size is too large. Please select a smaller image');
              throw new Error('File too large');
            default:
              toast.error(error.message || tErrors('graphql.default'));
              throw new Error(error.message || 'Upload failed');
          }
        }
        
        console.log('✅ Upload successful!');
        setLoading(false);
        return { data: result.data };
        
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError instanceof Error && fetchError.name === 'AbortError') {
          throw new Error('Upload timed out after 30 seconds');
        }
        throw fetchError;
      }
      
    } catch (error) {
      console.error('💥 Upload error:', error);
      setLoading(false);
      
      // If we haven't already shown a specific error message, show a generic one
      if (error instanceof Error) {
        if (!error.message.includes('Unauthorized') && 
            !error.message.includes('Validation') && 
            !error.message.includes('File too large')) {
          toast.error(`Upload failed: ${error.message}`);
        }
      } else {
        toast.error(tErrors('graphql.default'));
      }
      
      throw error;
    }
  }, [tErrors]);

  return [uploadProfilePhoto, { loading }] as const;
}; 